import React, { useState } from 'react';
import CustomModal from '../reusable/CustomModal';
import CustomButton from '../reusable/CustomButton';
import { executeWorkflowTransition, WorkflowTransitionResponse } from '../../services/workflowService';
import useUserDetails from '../hooks/useUserDetails';
import useErrorModal from '../hooks/useErrorModal';
import { USER_ROLES } from '../../constants';
import './ApproveDataQcModal.css';

interface ApproveDataQcModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (transitionResponse: WorkflowTransitionResponse) => void;
}

const ApproveDataQcModal = ({ isOpen, onClose, onSuccess }: ApproveDataQcModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const { handleOpen } = useErrorModal();

  const handleApprove = async () => {
    if (!userDetails || !userDetails.eoaId || !userDetails.access_token) {
      handleOpen('User details not available.', new Error('User details not available.'));
      return;
    }

    setIsLoading(true);
    try {
      const now = new Date().toISOString();

      const metadata = {
        district_data_qc_approved_at: now,
        district_data_qc_approved_by: userDetails.name || 'Unknown',
        district_data_qc_approved_by_role: userDetails.role || 'Unknown',
      };

      const transitionResponse = await executeWorkflowTransition(
        userDetails.eoaId,
        userDetails.access_token,
        'district_data_qc_approval',
        metadata
      );

      onSuccess(transitionResponse);
    } catch (error) {
      handleOpen('Error occurred while approving data quality check', error as Error);
      setIsLoading(false);
    }
  };

  const getMessage = () => {
    if (!userDetails) return 'By confirming, you are <b>approving</b> the data quality check for <b>all</b> schools.';

    if (userDetails.role === USER_ROLES.DISTRICT_DATA_LEAD) {
      return 'By confirming, you are <b>approving</b> the data quality check for <b>all</b> schools.';
    } else {
      return 'The District Data Lead has <b>not</b> approved the school report yet.<br/><br/> By confirming, you will approve the data quality check for <b>all</b> schools, and the District Data Lead will no longer have the option to approve or reject it.';
    }
  };

  return (
    <CustomModal isOpen={isOpen} handleClose={onClose} title="Are you sure?" icon={false} maxWidth="sm">
      <p className="approve-data-qc-description" dangerouslySetInnerHTML={{ __html: getMessage() }} />
      <div className="confirmation-buttons">
        <CustomButton variant="secondary" buttonText="Cancel" buttonAction={onClose} disabled={isLoading} />
        <CustomButton variant="primary" buttonText="Confirm" buttonAction={handleApprove} loading={isLoading} />
      </div>
    </CustomModal>
  );
};

export default ApproveDataQcModal;
